import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFxIndex, apiFxQuote, type FxAccount, type FxQuote } from "../lib/api";

export default function FxCreate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<FxAccount[]>([]);
  const [targets, setTargets] = useState<string[]>([]);
  const [fromCurrency, setFromCurrency] = useState("");
  const [toCurrency, setToCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;

    apiFxIndex()
      .then((res) => {
        if (!alive) return;

        const nextAccounts = res.accounts ?? [];
        const nextTargets = res.to_currencies ?? [];
        setAccounts(nextAccounts);
        setTargets(nextTargets);

        const firstFrom = nextAccounts[0]?.currency ?? "";
        setFromCurrency(firstFrom);
        setToCurrency(nextTargets.find((code) => code !== firstFrom) ?? "");
      })
      .catch((err: any) => setError(err?.message || "Could not load FX options."))
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const sourceAccount = useMemo(
    () => accounts.find((account) => account.currency === fromCurrency) ?? null,
    [accounts, fromCurrency]
  );

  const availableTargets = useMemo(
    () => targets.filter((code) => code !== fromCurrency),
    [targets, fromCurrency]
  );

  useEffect(() => {
    if (toCurrency === fromCurrency || !availableTargets.includes(toCurrency)) {
      setToCurrency(availableTargets[0] ?? "");
    }
  }, [availableTargets, fromCurrency, toCurrency]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setError("Enter an amount greater than zero.");
      return;
    }

    if (sourceAccount && value > Number(sourceAccount.available_balance ?? sourceAccount.balance)) {
      setError("Amount is higher than your available source balance.");
      return;
    }

    setSubmitting(true);
    try {
      const quote: FxQuote = await apiFxQuote({
        from_currency: fromCurrency,
        to_currency: toCurrency,
        from_amount: value,
      });

      const payload = { quote, request: { from_currency: fromCurrency, to_currency: toCurrency, from_amount: value } };
      sessionStorage.setItem("finexa.fx.quote", JSON.stringify(payload));
      navigate("/fx/confirm", { state: payload });
    } catch (err: any) {
      setError(err?.message || "Could not fetch the FX quote.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="section-box skeleton">Loading FX...</div>;
  }

  return (
    <div className="max-w-3xl card-box space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Currency Exchange</h1>
        <p className="text-sm text-gray-500">
          Exchange from an approved funded account into another enabled currency or crypto balance.
        </p>
      </header>

      {error && <div className="alert-danger">{error}</div>}

      {accounts.length === 0 ? (
        <div className="alert-info">
          No funded FX source accounts are available. Add funds to an enabled currency account first.
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="label">From currency</label>
            <select
              className="input w-full"
              value={fromCurrency}
              onChange={(event) => setFromCurrency(event.target.value)}
              required
            >
              {accounts.map((account) => (
                <option key={account.id} value={account.currency}>
                  {account.currency} - Balance: {formatMoney(account.balance)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">To currency</label>
            <select
              className="input w-full"
              value={toCurrency}
              onChange={(event) => setToCurrency(event.target.value)}
              required
            >
              {availableTargets.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Amount to exchange</label>
            <input
              className="input w-full"
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              required
            />
            {sourceAccount && (
              <p className="mt-1 text-xs text-gray-500">
                Available: {formatMoney(sourceAccount.available_balance ?? sourceAccount.balance)} {fromCurrency}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button className="btn" disabled={submitting || !toCurrency}>
              {submitting ? "Fetching quote..." : "Get live rate"}
            </button>
            <Link to="/transfers" className="btn btn-white">
              Cancel
            </Link>
          </div>
        </form>
      )}
    </div>
  );
}

function formatMoney(value: number | string | null | undefined) {
  return Number(value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
